import type { IsoTimestamp, SyncedRow, SyncedTable, Uuid } from "../types/base.js";
import { SYNCED_TABLES } from "../types/base.js";
import { nextAttemptAt } from "./backoff.js";
import { mergeRows } from "./merge.js";
import { TransportError } from "./types.js";
import type { OutboxEntry, RemoteAdapter, StorageAdapter, SyncState } from "./types.js";

export interface SyncOptions {
  /** Wie viele Outbox-Einträge ein Upload-Stapel umfasst. */
  pushBatchSize?: number;
  /** Wie viele Zeilen ein Pull je Tabelle und Runde holt. */
  pullPageSize?: number;
  /** Nach wie vielen Fehlversuchen ein Eintrag als aussichtslos gilt. */
  maxAttempts?: number;
  now?: () => Date;
}

export interface SyncResult {
  pushed: number;
  pulled: number;
  failed: number;
  errors: string[];
}

const DEFAULTS = { pushBatchSize: 50, pullPageSize: 500, maxAttempts: 12 } as const;

/**
 * Reihenfolge beim Hochladen. Nicht dieselbe wie SYNCED_TABLES, denn die
 * Fremdschlüssel geben sie vor: Ein Artikel zeigt auf sein Produkt und seine
 * Liste, eine Einkaufsposition auf ihren Einkauf.
 *
 * Das war der Fehler vom 19.09.2026: items stand vor products, jeder Artikel
 * trug ein product_id, und der Upsert scheiterte an einem Fremdschlüssel, der
 * erst eine Zeile später entstanden wäre.
 */
const PUSH_REIHENFOLGE: readonly SyncedTable[] = [
  "families",
  "members",
  "invites",
  "stores",
  "products",
  "lists",
  "items",
  "trips",
  "trip_items",
  "price_entries",
  "recipes",
  "recipe_ingredients",
  "pantry_items",
  "meal_plan_entries",
  // Wird nur gelesen: Die Outbox bleibt leer, weil das Depot nie
  // writeWithOutbox darauf ruft. Der Rang hier kostet nichts.
  "partner_connections",
];

function tabellenRang(table: SyncedTable): number {
  const rang = PUSH_REIHENFOLGE.indexOf(table);
  return rang === -1 ? PUSH_REIHENFOLGE.length : rang;
}

/**
 * Die Sync-Engine. Sie kennt weder Dexie noch Supabase, nur die beiden
 * Adapter. Dadurch läuft sie unverändert unter Expo (Stufe 6) und in Tests
 * ohne Netz.
 *
 * Ablauf einer Runde: erst hochladen, was wartet, dann herunterladen, was sich
 * geändert hat. Diese Reihenfolge ist wichtig. Andersherum würde ein eigener,
 * noch nicht übertragener Stand von einer älteren Serverfassung überschrieben.
 */
export class SyncEngine {
  private readonly options: Required<SyncOptions>;
  private running = false;

  constructor(
    private readonly storage: StorageAdapter,
    private readonly remote: RemoteAdapter,
    options: SyncOptions = {},
  ) {
    this.options = {
      pushBatchSize: options.pushBatchSize ?? DEFAULTS.pushBatchSize,
      pullPageSize: options.pullPageSize ?? DEFAULTS.pullPageSize,
      maxAttempts: options.maxAttempts ?? DEFAULTS.maxAttempts,
      now: options.now ?? (() => new Date()),
    };
  }

  /** Läuft eine Runde. Mehrfachaufrufe überlappen nicht. */
  async runOnce(): Promise<SyncResult> {
    if (this.running) return { pushed: 0, pulled: 0, failed: 0, errors: [] };
    this.running = true;
    try {
      const push = await this.push();
      const pull = await this.pull();
      return {
        pushed: push.pushed,
        pulled: pull.pulled,
        failed: push.failed,
        errors: [...push.errors, ...pull.errors],
      };
    } finally {
      this.running = false;
    }
  }

  /**
   * Lädt die Outbox hoch. Einträge werden nach Tabelle gebündelt, weil ein
   * Upsert je Tabelle einen Roundtrip spart. Scheitert ein Stapel, bekommt
   * jeder Eintrag darin einen neuen Versuchszeitpunkt mit Backoff.
   */
  async push(): Promise<{ pushed: number; failed: number; errors: string[] }> {
    const now = this.options.now();
    const due = await this.storage.takeDue(now.toISOString(), this.options.pushBatchSize);
    if (due.length === 0) return { pushed: 0, failed: 0, errors: [] };

    let pushed = 0;
    let failed = 0;
    const errors: string[] = [];

    for (const [key, entries] of groupByTable(due)) {
      const [table, operation] = key.split("\u0000") as [SyncedTable, "upsert" | "update"];
      // Bei mehreren Einträgen zur selben Zeile zählt nur der jüngste Stand.
      const latest = newestPerRow(entries);

      let stapelFehler: unknown = null;
      try {
        await this.senden(table, operation, latest.map((entry) => entry.payload));
        await this.storage.markSent(entries.map((entry) => entry.id));
        pushed += entries.length;
        continue;
      } catch (error) {
        stapelFehler = error;
        errors.push(`${table}: ${error instanceof Error ? error.message : String(error)}`);
      }

      // Netz weg oder Server überlastet: Einzelversuche wären genauso
      // vergeblich und kosten je wartender Zeile eine Anfrage.
      if (stapelFehler instanceof TransportError) {
        const message = stapelFehler.message;
        for (const eintrag of entries) {
          const versuche = eintrag.attempts + 1;
          await this.storage.markFailed(
            eintrag.id,
            message,
            versuche >= this.options.maxAttempts ? farFuture(now) : nextAttemptAt(versuche, now),
          );
          failed += 1;
        }
        continue;
      }

      /*
        Der Stapel ist gescheitert. Jetzt einzeln.

        PostgREST schreibt einen Stapel ganz oder gar nicht. Eine einzige
        kaputte Zeile, etwa mit einem created_by, das es in auth.users nicht
        gibt, nahm bisher alle anderen mit in den Fehlversuch und nach zwölf
        Runden für ein Jahr aus dem Verkehr. Am 19.09.2026 standen so 39
        Artikel auf einem Telefon, von denen genau einer schuldig war.

        Der zweite Durchgang kostet je Zeile einen Aufruf, läuft aber nur nach
        einem Fehlschlag. Danach steht genau der faule Eintrag still, und man
        sieht in den Einstellungen, welcher es ist.
      */
      const nachRow = new Map(latest.map((entry) => [entry.row_id, entry.payload]));
      for (const entry of entries) {
        const zeile = nachRow.get(entry.row_id);
        try {
          if (zeile) await this.senden(table, operation, [zeile]);
          await this.storage.markSent([entry.id]);
          pushed += 1;
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          const attempts = entry.attempts + 1;
          if (attempts >= this.options.maxAttempts) {
            // Aufgeben heißt nicht wegwerfen: der Eintrag bleibt mit seinem
            // Fehler stehen, damit die Einstellungen ihn anzeigen können.
            await this.storage.markFailed(entry.id, message, farFuture(now));
          } else {
            await this.storage.markFailed(entry.id, message, nextAttemptAt(attempts, now));
          }
          failed += 1;
        }
      }
    }

    return { pushed, failed, errors };
  }

  private async senden(table: SyncedTable, operation: "upsert" | "update", rows: readonly SyncedRow[]): Promise<void> {
    if (rows.length === 0) return;
    if (operation === "update") await this.remote.update(table, rows);
    else await this.remote.upsert(table, rows);
  }

  /**
   * Holt je Tabelle alles, was seit dem letzten Cursor geändert wurde, und
   * schreibt nur die Zeilen, die den Konflikt gewinnen. Der Cursor wandert
   * erst weiter, wenn die Seite geschrieben ist.
   */
  async pull(tables: readonly SyncedTable[] = SYNCED_TABLES): Promise<{ pulled: number; errors: string[] }> {
    let pulled = 0;
    const errors: string[] = [];

    for (const table of tables) {
      try {
        const cursor = await this.storage.readCursor(table);
        const incoming = await this.remote.fetchSince(table, cursor.updated_at, this.options.pullPageSize);
        if (incoming.length === 0) continue;

        const local = await this.storage.readRows(
          table,
          incoming.map((row) => row.id),
        );
        const writes = mergeRows(local, incoming);
        if (writes.length > 0) await this.storage.writeRows(table, writes);

        await this.storage.writeCursor({ table, updated_at: maxUpdatedAt(incoming, cursor.updated_at) });
        pulled += writes.length;
      } catch (error) {
        errors.push(`${table}: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    return { pulled, errors };
  }

  async state(online: boolean): Promise<SyncState> {
    return {
      phase: this.running ? "push" : "idle",
      online,
      pending: await this.storage.countPending(),
      last_success_at: null,
      last_error: null,
    };
  }
}

/**
 * Bündelt nach Tabelle und Schreibart, damit ein Aufruf je Gruppe reicht, und
 * sortiert die Gruppen entlang der Fremdschlüssel.
 */
function groupByTable(entries: readonly OutboxEntry[]): Map<string, OutboxEntry[]> {
  const grouped = new Map<string, OutboxEntry[]>();
  for (const entry of entries) {
    const key = `${entry.table}\u0000${entry.operation ?? "upsert"}`;
    const bucket = grouped.get(key);
    if (bucket) bucket.push(entry);
    else grouped.set(key, [entry]);
  }

  return new Map(
    [...grouped.entries()].sort(([a], [b]) => tabellenRang(a.split("\u0000")[0] as SyncedTable) - tabellenRang(b.split("\u0000")[0] as SyncedTable)),
  );
}

function newestPerRow(entries: readonly OutboxEntry[]): OutboxEntry[] {
  const byRow = new Map<Uuid, OutboxEntry>();
  for (const entry of entries) {
    const existing = byRow.get(entry.row_id);
    if (!existing || Date.parse(entry.client_updated_at) >= Date.parse(existing.client_updated_at)) {
      byRow.set(entry.row_id, entry);
    }
  }
  return [...byRow.values()];
}

function maxUpdatedAt(rows: readonly SyncedRow[], fallback: IsoTimestamp | null): IsoTimestamp | null {
  let max = fallback;
  for (const row of rows) {
    if (max === null || Date.parse(row.updated_at) > Date.parse(max)) max = row.updated_at;
  }
  return max;
}

function farFuture(now: Date): IsoTimestamp {
  return new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000).toISOString();
}
